import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Minus, Plus, Loader2, ShoppingBag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const loadRazorpay = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

export default function ShopCheckout() {
  const [products, setProducts] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [qty, setQty] = useState({});
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/payments/products")
      .then((r) => { setProducts(r.data.products); setEnabled(r.data.enabled); })
      .catch(() => setEnabled(false));
  }, []);

  const setQ = (id, v) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + v) }));
  const total = products.reduce((s, p) => s + p.price * (qty[p.id] || 0), 0);

  const pay = async () => {
    if (!customer.name.trim() || !customer.phone.trim()) { toast.error("Enter your name and phone"); return; }
    const items = products.filter((p) => (qty[p.id] || 0) > 0).map((p) => ({ id: p.id, qty: qty[p.id] }));
    if (!items.length) { toast.error("Select at least one item"); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/payments/create-order", { items, customer });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment window"); setPaying(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.razorpay_order_id, amount: data.amount * 100, currency: data.currency,
        name: "Creative Prints", description: "Print order",
        prefill: { name: customer.name, contact: customer.phone },
        theme: { color: "#D97706" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", { ...resp, our_order_id: data.our_order_id });
            setDone(true);
          } catch { toast.error("Payment verification failed — contact us if money was deducted"); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed"); setPaying(false); });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
        <div className="max-w-md text-center rounded-2xl border border-slate-200 bg-white p-10">
          <CheckCircle2 size={56} className="mx-auto text-green-500" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900">Payment successful!</h1>
          <p className="mt-2 text-slate-600">Thank you, {customer.name}. Our team will contact you on {customer.phone} to confirm artwork and delivery.</p>
          <Link to="/"><Button className="mt-6 bg-primary hover:bg-amber-700 text-white">Back to website</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-md bg-primary text-white"><Printer size={18} /></span><span className="font-heading font-extrabold text-slate-900">Order Online</span></Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-primary flex items-center gap-1"><ArrowLeft size={15} /> Website</Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8">
        {!enabled && (
          <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Online payment is not active yet. Please use Call / WhatsApp / Get Quote for now.</div>
        )}
        <h1 className="font-heading text-2xl font-bold text-slate-900">Ready-to-print products</h1>
        <p className="mt-1 text-sm text-slate-500">Fixed-price items you can pay for online. Custom jobs stay on quote — use Get Quote or the Design Studio.</p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {products.map((p) => (
            <div key={p.id} data-testid={`shop-product-${p.id}`} className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-semibold text-slate-900">{p.name}</h3>
              <p className="mt-1 text-lg font-extrabold text-primary">₹{p.price}</p>
              <div className="mt-3 flex items-center gap-3">
                <button data-testid={`qty-minus-${p.id}`} onClick={() => setQ(p.id, -1)} className="grid h-8 w-8 place-items-center rounded-md border border-slate-300"><Minus size={15} /></button>
                <span data-testid={`qty-${p.id}`} className="w-8 text-center font-bold">{qty[p.id] || 0}</span>
                <button data-testid={`qty-plus-${p.id}`} onClick={() => setQ(p.id, 1)} className="grid h-8 w-8 place-items-center rounded-md border border-slate-300"><Plus size={15} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input data-testid="shop-name" className="mt-1.5" value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} /></div>
            <div><Label>Phone / WhatsApp</Label><Input data-testid="shop-phone" className="mt-1.5" value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} /></div>
          </div>
          <div className="mt-5 flex items-center justify-between">
            <span className="text-lg font-bold text-slate-900" data-testid="shop-total">Total: ₹{total}</span>
            <Button data-testid="shop-pay" onClick={pay} disabled={paying || !enabled || total <= 0} className="gap-2 bg-primary hover:bg-amber-700 text-white h-11 px-6 font-semibold">
              {paying ? <><Loader2 size={18} className="animate-spin" /> Processing…</> : <><ShoppingBag size={18} /> Pay ₹{total}</>}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
